import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

const ADMIN_SESSION_DURATION_MS = 20 * 60 * 1000;

interface AdminUser {
  _id: string;
  email: string;
  name: string;
  isAdmin: boolean;
}

interface AuthState {
  accessToken: string | null;
  adminUser: AdminUser | null;
  isAuthenticated: boolean;
  tokenSetAt: number | null;

  setAuth: (token: string, user: AdminUser) => void;
  clearAuth: () => void;
  getAccessToken: () => string | null;
  isSessionValid: () => boolean;
  extendSession: () => void;
}

/**
 * sessionStorage 에 영속화 — 새로고침에도 로그인이 유지되고, 탭/브라우저를
 * 닫으면 로그아웃된다 (어드민 특성상 localStorage 는 쓰지 않는다).
 * 20분 세션 TTL 은 tokenSetAt 이 함께 저장되므로 새로고침 후에도 그대로
 * 적용된다 (ProtectedRoute 의 isSessionValid 체크).
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      adminUser: null,
      isAuthenticated: false,
      tokenSetAt: null,

      setAuth: (token, user) => {
        set({
          accessToken: token,
          adminUser: user,
          isAuthenticated: user.isAdmin === true,
          tokenSetAt: Date.now(),
        });
      },

      clearAuth: () => {
        set({
          accessToken: null,
          adminUser: null,
          isAuthenticated: false,
          tokenSetAt: null,
        });
      },

      getAccessToken: () => get().accessToken,

      extendSession: () => {
        const { accessToken, tokenSetAt } = get();
        if (!accessToken || !tokenSetAt) return;

        set({ tokenSetAt: Date.now() });
      },

      isSessionValid: () => {
        const { tokenSetAt, accessToken } = get();
        if (!accessToken || !tokenSetAt) return false;

        const elapsed = Date.now() - tokenSetAt;
        return elapsed < ADMIN_SESSION_DURATION_MS;
      },
    }),
    {
      name: "novela-admin-auth",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        adminUser: state.adminUser,
        isAuthenticated: state.isAuthenticated,
        tokenSetAt: state.tokenSetAt,
      }),
    },
  ),
);
